import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PickupPaymentModal from '../components/PickupPaymentModal';
import {
    Truck,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    ChevronRight,
    AlertTriangle,
    Eye,
    Check,
    X,
    MessageSquare,
    Image as ImageIcon,
    Loader2,
    ClipboardList,
    Smartphone,
    CreditCard,
    Wallet,
    Banknote,
    Camera,
    ExternalLink,
    FileText,
    MapPin,
    RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';

const RouteSettlementPage = () => {
    const { token } = useAuth();
    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSettling, setIsSettling] = useState(false);
    const [expenses, setExpenses] = useState([]);
    const [orders, setOrders] = useState([]);
    const [paymentSummary, setPaymentSummary] = useState({});
    const [additionalStops, setAdditionalStops] = useState([]);
    const [returns, setReturns] = useState([]);

    // Settlement Form State
    const [receivedCash, setReceivedCash] = useState('');
    const [notes, setNotes] = useState('');
    const [approvedExpenses, setApprovedExpenses] = useState([]);
    const [rejectedExpenses, setRejectedExpenses] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedPaymentOrder, setSelectedPaymentOrder] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [additionalExpenseAmount, setAdditionalExpenseAmount] = useState('');
    const [additionalExpenseDesc, setAdditionalExpenseDesc] = useState('');
    const [additionalExpenseFile, setAdditionalExpenseFile] = useState(null);
    const [activeTab, setActiveTab] = useState('pending');
    const [settledRoutes, setSettledRoutes] = useState([]);
    const [selectedSettlement, setSelectedSettlement] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');

    useEffect(() => {
        fetchPendingRoutes();
    }, []);

    const fetchPendingRoutes = async () => {
        try {
            setIsLoading(true);
            const res = await fetch('/api/routes/pending-settlement', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setRoutes(data.data);
            }
        } catch (error) {
            console.error('Error fetching pending routes:', error);
            toast.error('Error al cargar rutas pendientes');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSettledRoutes = async () => {
        try {
            setIsLoading(true);
            const res = await fetch('/api/routes/settled', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSettledRoutes(data.settlements);
            }
        } catch (error) {
            console.error('Error fetching settled routes:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSettlementDetail = async (routeId) => {
        try {
            setIsLoadingDetail(true);
            const res = await fetch(`/api/routes/${routeId}/settlement`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSelectedSettlement(data);
            }
        } catch (error) {
            console.error('Error fetching settlement detail:', error);
            toast.error('Error al cargar detalle');
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'history' && settledRoutes.length === 0) {
            fetchSettledRoutes();
        }
    };

    const fetchRouteDetails = async (route) => {
        try {
            setSelectedRoute(route);
            setIsLoading(true);

            // receivedCash starts empty — cajera must type it manually for cash verification
            setReceivedCash('');
            setNotes('');
            setApprovedExpenses([]);
            setRejectedExpenses([]);
            setPaymentSummary({});
            setAdditionalStops([]);

            // Fetch Expenses
            const expRes = await fetch(`/api/routes/${route.id}/expenses`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const expData = await expRes.json();
            if (expData.success) {
                setExpenses(expData.expenses);
            }

            // Fetch Orders
            const ordRes = await fetch(`/api/routes/${route.id}/orders`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!ordRes.ok) {
                throw new Error(`Error ${ordRes.status} al obtener pedidos`);
            }

            const ordData = await ordRes.json();
            if (ordData.success) {
                setOrders(ordData.data);
                setPaymentSummary(ordData.payment_summary || {});
                setAdditionalStops(ordData.additional_stops || []);
            } else {
                throw new Error(ordData.message || 'Error al obtener pedidos');
            }

        } catch (error) {
            console.error('Error fetching route details:', error);
            toast.error(error.message || 'Error al cargar detalles de la ruta');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApproveExpense = (id) => {
        setApprovedExpenses(prev => [...prev, id]);
        setRejectedExpenses(prev => prev.filter(eid => eid.expenseId !== id));
    };

    const handleRejectExpense = (id, reason = 'No justificado') => {
        setRejectedExpenses(prev => [...prev.filter(eid => eid.expenseId !== id), { expenseId: id, reason }]);
        setApprovedExpenses(prev => prev.filter(eid => eid !== id));
    };

    // Handler for PickupPaymentModal onConfirm
    const confirmPickupPayment = async ({ orderId, payments }) => {
        const fd = new FormData();
        fd.append('orderId', orderId);

        const paymentsMeta = payments.map((p, idx) => ({
            method: p.method,
            amount: p.amount,
            linkedMovementIds: p.linkedMovementIds,
            photoIndices: p.files.map((_, fIdx) => `${idx}_${fIdx}`)
        }));
        fd.append('payments', JSON.stringify(paymentsMeta));
        fd.append('notes', 'Pago registrado desde Liquidación de Rutas');

        payments.forEach((p) => {
            p.files.forEach((file) => {
                fd.append('photos', file);
            });
        });

        try {
            const res = await fetch('/api/logistics/receive-pickup-payment', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error registrando pago');
            toast.success(data.message || 'Pagos registrados correctamente');
            setShowPaymentModal(false);
            setSelectedPaymentOrder(null);
            // Refresh route details to update balances
            if (selectedRoute) fetchRouteDetails(selectedRoute);
        } catch (err) {
            console.error('Error registrando pago:', err);
            toast.error(err.message || 'Error al registrar el pago');
            throw err;
        }
    };

    const handleReturnToPackaging = async (order) => {
        if (!window.confirm(`¿Confirmar retorno del pedido ${order.order_number}? Se marcará como No Entregado y será visible en Facturación para gestión.`)) return;
        try {
            const res = await fetch('/api/logistics/return-undelivered', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ orderId: order.id, reason: 'Retorno desde liquidación - no entregado' })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error al retornar');
            toast.success(`Pedido ${order.order_number} marcado como No Entregado`);
            if (selectedRoute) fetchRouteDetails(selectedRoute);
        } catch (err) {
            console.error('Error retornando pedido:', err);
            toast.error(err.message || 'Error al retornar pedido');
        }
    };

    const handleSettle = async () => {
        if (!receivedCash && receivedCash !== 0) {
            toast.error('Debe ingresar el efectivo recibido');
            return;
        }

        // Compute pending approval: expenses not yet approved or rejected
        const allExpenseIds = expenses.map(e => e.id);
        const processedIds = [...approvedExpenses, ...rejectedExpenses.map(r => r.expenseId)];
        const pendingApprovalList = allExpenseIds.filter(id => !processedIds.includes(id));

        if (pendingApprovalList.length > 0) {
            toast.error('Debe aprobar o rechazar todos los gastos');
            return;
        }

        // --- VALIDATION GUARD (Item 4) ---
        const cashValue = parseFloat(receivedCash) || 0;
        // Compute expected cash from payment summary and expenses (same as JSX render)
        const cashFromRC = parseFloat(paymentSummary?.efectivo || 0);

        // [FIX] Consolidar efectivo reportado por mensajero que aún no ha sido asentado (especialmente útil para pedidos INT)
        const pendingReportedCash = (orders || []).reduce((sum, o) => {
            const reported = parseFloat(o.reporter_data?.cash || 0);
            const settled = (parseFloat(o.total_paid_amount || 0) + parseFloat(o.children_paid_amount || 0)) || 0;
            // Si el mensajero reportó haber cobrado efectivo pero aún no se ha registrado el pago en el sistema
            if (reported > settled) {
                return sum + (reported - settled);
            }
            return sum;
        }, 0);

        const totalAllExpenses = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const efectivoNetoEsperado = (cashFromRC + pendingReportedCash) - totalAllExpenses;
        const diffEsperado = cashValue - efectivoNetoEsperado;
        const diffDeclarado = cashValue - parseFloat(selectedRoute.declared_cash || 0);

        if (Math.abs(diffEsperado) > 50000 || Math.abs(diffDeclarado) > 50000) {
            const confirmed = window.confirm(
                `🚨 ATENCIÓN: Desbalance detectado.\n\n` +
                `Efectivo Recibido: ${formatCurrency(cashValue)}\n` +
                `Esperado (Sistema + Reporte Mensajero): ${formatCurrency(efectivoNetoEsperado)}\n` +
                `Diferencia vs Sistema: ${formatCurrency(diffEsperado)}\n` +
                `Diferencia vs Mensajero: ${formatCurrency(diffDeclarado)}\n\n` +
                `¿Está seguro de liquidar la ruta con este descuadre?`
            );
            if (!confirmed) return;
        }

        // Validate: if additional expense > 0, receipt is mandatory
        const addlExp = parseFloat(additionalExpenseAmount) || 0;
        if (addlExp > 0 && !additionalExpenseFile) {
            toast.error('Debe adjuntar un soporte para el gasto adicional');
            return;
        }

        try {
            setIsSettling(true);

            // Use FormData to support file upload
            const formData = new FormData();
            formData.append('receivedCash', parseFloat(receivedCash));
            formData.append('approvedExpenses', JSON.stringify(approvedExpenses));
            formData.append('rejectedExpenses', JSON.stringify(rejectedExpenses));
            formData.append('notes', notes || '');
            if (addlExp > 0) {
                formData.append('additionalExpenseAmount', addlExp);
                formData.append('additionalExpenseDesc', additionalExpenseDesc || '');
                formData.append('additionalExpenseReceipt', additionalExpenseFile);
            }

            const res = await fetch(`/api/routes/${selectedRoute.id}/settle`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await res.json();
            if (data.success) {
                toast.success('Ruta liquidada exitosamente');
                setSelectedRoute(null);
                setAdditionalExpenseFile(null);
                fetchPendingRoutes();
            } else {
                toast.error(data.message || 'Error al liquidar ruta');
            }
        } catch (error) {
            console.error('Error settling route:', error);
            toast.error('Error de red al liquidar ruta');
        } finally {
            setIsSettling(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(val || 0);
    };

    if (isLoading && !selectedRoute) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
                <p className="text-gray-500 font-medium">Cargando rutas pendientes...</p>
            </div>
        );
    }

    // --- HISTORY TAB ---
    const renderHistoryTab = () => {
        if (selectedSettlement) {
            return renderSettlementReport();
        }

        const fmt = (v) => Number(v || 0).toLocaleString();

        // ── Driver cost constants (same as profitability section) ──
        const FACTOR_PRESTACIONAL = 1.51;
        const DAILY_FULL_COST = Math.round(((1423500 + 200000) * FACTOR_PRESTACIONAL) / 30); // ≈ $81,718
        const STOP_COST = Math.round(2500 * FACTOR_PRESTACIONAL); // ≈ $3,775
        const DAILY_FUEL = 35000; // Estimado ACPM diario

        // ── Filter by date range ──
        const filtered = settledRoutes.filter(s => {
            if (!filterFrom && !filterTo) return true;
            const d = s.route_date?.slice(0, 10);
            if (filterFrom && d < filterFrom) return false;
            if (filterTo && d > filterTo) return false;
            return true;
        });

        // ── Messenger-level summary (from filtered routes) ──
        const messengerSummary = {};
        filtered.forEach(s => {
            const name = s.messenger_name || 'Sin asignar';
            if (!messengerSummary[name]) {
                messengerSummary[name] = { routes: 0, stops: 0, totalValue: 0, deliveryFees: 0, fuel: 0, expenses: 0, driverCost: 0, expectedCash: 0 };
            }
            const m = messengerSummary[name];
            const routeStops = Number(s.total_stops || s.total_orders || 0);
            // Detect ACPM in additional_expense
            const additionalFuel = (s.additional_expense_desc || '').toLowerCase().includes('acpm') || (s.additional_expense_desc || '').toLowerCase().includes('combustible')
                ? Number(s.additional_expense_amount || 0) : 0;
            m.routes++;
            m.stops += routeStops;
            m.totalValue += Number(s.total_order_value || 0);
            m.deliveryFees += Number(s.delivery_fees_net || 0);
            m.fuel += Number(s.fuel_expenses || 0) + additionalFuel;
            m.expenses += Number(s.total_expenses || 0);
            m.driverCost += DAILY_FULL_COST + (routeStops * STOP_COST);
            m.expectedCash += Number(s.expected_cash || 0);
        });

        return (
            <div>
                {filtered.length === 0 ? (
                    <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
                        <ClipboardList className="text-gray-300 w-10 h-10 mx-auto mb-3" />
                        <h3 className="text-lg font-bold text-gray-800">No hay liquidaciones</h3>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* ── Date Filter ── */}
                        <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-bold text-gray-500">📅 Filtro:</span>
                            {(() => {
                                const now = new Date();
                                const y = now.getFullYear();
                                const m = String(now.getMonth() + 1).padStart(2, '0');
                                const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
                                const presets = [
                                    { label: '1ra Quincena', from: `${y}-${m}-01`, to: `${y}-${m}-15` },
                                    { label: '2da Quincena', from: `${y}-${m}-16`, to: `${y}-${m}-${lastDay}` },
                                    { label: 'Mes completo', from: `${y}-${m}-01`, to: `${y}-${m}-${lastDay}` },
                                ];
                                // Previous month presets
                                const pm = now.getMonth() === 0 ? 12 : now.getMonth();
                                const py = now.getMonth() === 0 ? y - 1 : y;
                                const pmStr = String(pm).padStart(2, '0');
                                const pmLastDay = new Date(py, pm, 0).getDate();
                                presets.push(
                                    { label: `1ra Q ${pmStr}/${py}`, from: `${py}-${pmStr}-01`, to: `${py}-${pmStr}-15` },
                                    { label: `2da Q ${pmStr}/${py}`, from: `${py}-${pmStr}-16`, to: `${py}-${pmStr}-${pmLastDay}` },
                                );
                                return presets.map(p => (
                                    <button
                                        key={p.label}
                                        onClick={() => { setFilterFrom(p.from); setFilterTo(p.to); }}
                                        className={`text-[10px] px-2 py-1 rounded-full border font-bold transition-all ${filterFrom === p.from && filterTo === p.to
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-blue-50'
                                            }`}
                                    >
                                        {p.label}
                                    </button>
                                ));
                            })()}
                            <div className="flex items-center gap-1 ml-auto">
                                <input
                                    type="date"
                                    value={filterFrom}
                                    onChange={e => setFilterFrom(e.target.value)}
                                    className="text-xs border border-gray-200 rounded px-2 py-1"
                                />
                                <span className="text-gray-400 text-xs">a</span>
                                <input
                                    type="date"
                                    value={filterTo}
                                    onChange={e => setFilterTo(e.target.value)}
                                    className="text-xs border border-gray-200 rounded px-2 py-1"
                                />
                                {(filterFrom || filterTo) && (
                                    <button
                                        onClick={() => { setFilterFrom(''); setFilterTo(''); }}
                                        className="text-[10px] text-red-500 hover:text-red-700 font-bold ml-1"
                                    >
                                        ✕ Limpiar
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Messenger Summary Header ── */}
                        {Object.entries(messengerSummary).map(([name, m]) => {
                            // Breakeven calculation
                            const avgFeePerStop = m.stops > 0 ? m.deliveryFees / m.stops : 0;
                            const netRevenuePerStop = avgFeePerStop - STOP_COST; // what each extra stop earns net
                            const dailyFixedCost = DAILY_FULL_COST + DAILY_FUEL;
                            const breakevenStops = netRevenuePerStop > 0 ? Math.ceil(dailyFixedCost / netRevenuePerStop) : 0;
                            return (
                                <div key={name} className="bg-slate-800 rounded-xl p-4 text-white">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">👤</span>
                                            <h3 className="font-black text-sm">{name}</h3>
                                            <span className="text-[10px] text-slate-400">{m.routes} ruta(s) • {m.stops} paradas</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                                        <div>
                                            <p className="text-[9px] text-slate-400 uppercase">Valor Operación</p>
                                            <p className="text-sm font-bold text-slate-200">$ {fmt(m.totalValue)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] text-green-400 uppercase">💵 Efectivo Bodega</p>
                                            <p className="text-sm font-bold text-green-300">$ {fmt(m.expectedCash)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] text-blue-400 uppercase">🚚 Domicilios (sin IVA)</p>
                                            <p className="text-sm font-bold text-blue-300">$ {fmt(m.deliveryFees)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] text-yellow-400 uppercase">👤 Costo Domiciliario</p>
                                            <p className="text-sm font-bold text-yellow-300">$ {fmt(m.driverCost)}</p>
                                            <p className="text-[8px] text-slate-500">{m.routes} día(s) × ${fmt(DAILY_FULL_COST)} + {m.stops}p × ${fmt(STOP_COST)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] text-orange-400 uppercase">⛽ Combustible</p>
                                            <p className="text-sm font-bold text-orange-300">$ {fmt(m.fuel)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] text-red-400 uppercase">💸 Gastos Totales</p>
                                            <p className="text-sm font-bold text-red-300">$ {fmt(m.expenses)}</p>
                                        </div>
                                        <div className="bg-slate-700 rounded-lg px-2 py-1">
                                            <p className="text-[9px] text-emerald-400 uppercase">🎯 Punto Equilibrio</p>
                                            <p className="text-sm font-black text-emerald-300">{breakevenStops} paradas/día</p>
                                            <p className="text-[8px] text-slate-500">Fijo: ${fmt(dailyFixedCost)}/d ÷ ${fmt(Math.round(netRevenuePerStop))}/p</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* ── Route Cards ── */}
                        {filtered.map(s => {
                            const pendingBalance = parseFloat(s.pending_balance || 0);
                            const routeStops = Number(s.total_stops || s.total_orders || 0);
                            const routeDriverCost = DAILY_FULL_COST + (routeStops * STOP_COST);
                            // ── Dato clave: Lo que DEBE recibir la cajera = Msj Declaró - Gastos ──
                            const gastosTotal = parseFloat(s.total_expenses || 0) + parseFloat(s.additional_expense_amount || 0);
                            const debeRecibirCajera = parseFloat(s.declared_cash || 0) - gastosTotal;
                            const difRealCajera = parseFloat(s.received_cash || 0) - debeRecibirCajera;
                            return (
                                <div
                                    key={s.settlement_id}
                                    onClick={() => fetchSettlementDetail(s.route_id)}
                                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer group"
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${pendingBalance > 0 ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-600'}`}>
                                                <CheckCircle size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900">{s.messenger_name}</h3>
                                                <p className="text-xs text-gray-500">Ruta #{s.route_number || s.route_id} • {new Date(s.route_date).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm flex-wrap">
                                            <div className="text-center">
                                                <p className="text-[10px] text-gray-400 uppercase font-bold">Pedidos</p>
                                                <p className="font-bold text-gray-800">{s.total_orders}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-gray-400 uppercase font-bold">Paradas</p>
                                                <p className="font-bold text-gray-800">{s.total_stops || s.total_orders}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-gray-400 uppercase font-bold">Valor Pedidos</p>
                                                <p className="font-bold text-gray-800">$ {fmt(s.total_order_value)}</p>
                                            </div>
                                            <div className="text-center bg-green-100 rounded-lg px-3 py-1.5 border border-green-300 shadow-sm">
                                                <p className="text-[10px] text-green-700 uppercase font-black">💵 Recibido Cajera</p>
                                                <p className="font-black text-green-800 text-lg">$ {fmt(s.received_cash)}</p>
                                            </div>
                                            {/* ── DATO CLAVE: Debe Recibir + Diferencia Real ── */}
                                            <div className="text-center bg-indigo-50 rounded-lg px-3 py-1.5 border border-indigo-300 shadow-sm">
                                                <p className="text-[10px] text-indigo-700 uppercase font-black">🎯 Debe Recibir</p>
                                                <p className="font-black text-indigo-800 text-lg">$ {fmt(debeRecibirCajera)}</p>
                                                {gastosTotal > 0 && (
                                                    <p className="text-[9px] text-indigo-500">Msj ${fmt(s.declared_cash)} − Gastos ${fmt(gastosTotal)}</p>
                                                )}
                                            </div>
                                            <div className={`text-center rounded-lg px-3 py-1.5 border shadow-sm ${Math.abs(difRealCajera) < 1 ? 'bg-emerald-50 border-emerald-300' : difRealCajera > 0 ? 'bg-blue-50 border-blue-300' : 'bg-red-50 border-red-300'}`}>
                                                <p className={`text-[10px] uppercase font-black ${Math.abs(difRealCajera) < 1 ? 'text-emerald-700' : difRealCajera > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                                    {Math.abs(difRealCajera) < 1 ? '✅ Cuadrada' : difRealCajera > 0 ? '📈 Sobrante' : '⚠ Faltante'}
                                                </p>
                                                <p className={`font-black text-lg ${Math.abs(difRealCajera) < 1 ? 'text-emerald-800' : difRealCajera > 0 ? 'text-blue-800' : 'text-red-800'}`}>
                                                    {Math.abs(difRealCajera) < 1 ? '$ 0' : `${difRealCajera > 0 ? '+' : ''}$ ${fmt(Math.abs(difRealCajera))}`}
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-purple-500 uppercase font-bold">🛵 Msj Declaró</p>
                                                <p className="font-bold text-purple-700">$ {fmt(s.declared_cash)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-blue-500 uppercase font-bold">📋 RC Sistema</p>
                                                <p className="font-bold text-blue-700">$ {fmt(s.expected_cash)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-blue-500 uppercase font-bold">🚚 Domicilios</p>
                                                <p className="font-bold text-blue-700">$ {fmt(s.delivery_fees_net)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-yellow-600 uppercase font-bold">👤 Costo Domic.</p>
                                                <p className="font-bold text-yellow-700">$ {fmt(routeDriverCost)}</p>
                                            </div>
                                            {Number(s.fuel_expenses || 0) > 0 && (
                                                <div className="text-center">
                                                    <p className="text-[10px] text-orange-500 uppercase font-bold">⛽ ACPM</p>
                                                    <p className="font-bold text-orange-600">$ {fmt(s.fuel_expenses)}</p>
                                                </div>
                                            )}
                                            {pendingBalance > 0 && (
                                                <div className="text-center bg-red-50 border border-red-200 rounded-lg px-3 py-1">
                                                    <p className="text-[10px] text-red-500 uppercase font-bold">⚠ Falta Cobrar</p>
                                                    <p className="font-black text-red-700">$ {fmt(pendingBalance)}</p>
                                                </div>
                                            )}
                                            {parseFloat(s.additional_expense_amount) > 0 && (
                                                <div className="text-center">
                                                    <p className="text-[10px] text-gray-400 uppercase font-bold">Gasto Adic.</p>
                                                    <p className="font-bold text-orange-600">-$ {fmt(s.additional_expense_amount)}</p>
                                                </div>
                                            )}
                                            <div className="text-center">
                                                <p className="text-[10px] text-gray-400 uppercase font-bold">Liquidado</p>
                                                <p className="text-[10px] text-gray-500">{new Date(s.settled_at).toLocaleString()}</p>
                                                <p className="text-[10px] text-gray-400">por {s.settled_by_name}</p>
                                            </div>
                                            <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500" />
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        );
    };

    // --- SETTLEMENT REPORT ---
    const renderSettlementReport = () => {
        if (isLoadingDetail || !selectedSettlement) {
            return (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
            );
        }

        const { settlement, route, orders, expenses: settledExpenses, additionalStops: settledStops, totalStops, paymentProofs, deliveryFeeStats } = selectedSettlement;
        const fmt = (v) => Number(v || 0).toLocaleString();

        return (
            <div>
                <button
                    onClick={() => setSelectedSettlement(null)}
                    className="mb-3 text-gray-500 hover:text-blue-600 font-medium flex items-center gap-1 text-sm"
                >
                    ← Volver al historial
                </button>

                <div id="settlement-report" className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    {/* Compact Header */}
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                <CheckCircle size={16} />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-gray-900">
                                    Ruta #{route?.route_number || route?.id} • {route?.messenger_name}
                                </h2>
                                <p className="text-[10px] text-gray-400">
                                    {new Date(route?.route_date).toLocaleDateString()} • Liquidado por {settlement.settled_by_name} el {new Date(settlement.settled_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => window.print()}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors print:hidden"
                        >
                            <FileText size={12} /> Imprimir
                        </button>
                    </div>

                    {/* Financial Summary - Compact */}
                    {(() => {
                        const activeOrders = orders?.filter(o => o.delivery_status !== 'failed') || [];
                        const totalValue = activeOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
                        const totalPaid = activeOrders.reduce((s, o) => s + parseFloat(o.paid_amount || 0), 0);
                        const totalPending = activeOrders.reduce((s, o) => s + Math.max(parseFloat(o.total_amount || 0) - parseFloat(o.paid_amount || 0), 0), 0);

                        const cashMethods = ['efectivo', 'contraentrega', 'efectivo_ruta_domicilio'];
                        const cashOrders = activeOrders.filter(o => cashMethods.includes(o.payment_method));
                        const transferOrders = activeOrders.filter(o => o.payment_method === 'transferencia');
                        const otherOrders = activeOrders.filter(o => !cashMethods.includes(o.payment_method) && o.payment_method !== 'transferencia');

                        const cashValue = cashOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
                        const transferValue = transferOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
                        const transferPaid = transferOrders.reduce((s, o) => s + parseFloat(o.paid_amount || 0), 0);
                        const otherValue = otherOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
                        const otherPaid = otherOrders.reduce((s, o) => s + parseFloat(o.paid_amount || 0), 0);

                        return (
                            <div className="px-5 py-4 border-b border-gray-100">

                                {/* Top: Value + Orders inline */}
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <p className="text-[10px] text-indigo-500 uppercase font-bold">Valor Total Ruta</p>
                                        <p className="text-2xl font-black text-indigo-800">$ {fmt(totalValue)}</p>
                                    </div>
                                    <div className="flex gap-4 text-right">
                                        <div>
                                            <p className="text-[10px] text-gray-400 uppercase font-bold">Pedidos</p>
                                            <p className="text-sm font-bold text-gray-800">{route?.total_orders} <span className="text-[10px] text-gray-400 font-normal">({route?.delivered_orders}✓ {route?.failed_orders}✗)</span></p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-gray-400 uppercase font-bold">Paradas</p>
                                            <p className="text-sm font-bold text-gray-800">{totalStops}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment breakdown compact */}
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] text-green-600 font-bold">💵 Efectivo</p>
                                            <p className="text-[10px] text-green-500">{cashOrders.length} ped.</p>
                                        </div>
                                        <p className="text-sm font-bold text-green-800">$ {fmt(cashValue)}</p>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] text-blue-600 font-bold">🏦 Transfer.</p>
                                            <p className="text-[10px] text-blue-500">{transferOrders.length} ped.</p>
                                        </div>
                                        <p className="text-sm font-bold text-blue-800">$ {fmt(transferValue)}</p>
                                        {transferPaid < transferValue && <p className="text-[10px] text-blue-500">Pagado: $ {fmt(transferPaid)}</p>}
                                    </div>
                                    <div className={`rounded-lg px-3 py-2 border ${otherOrders.length > 0 ? 'bg-purple-50 border-purple-200' : totalPending > 0 ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-200'}`}>
                                        {otherOrders.length > 0 ? (
                                            <>
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[10px] text-purple-600 font-bold">📋 Otros</p>
                                                    <p className="text-[10px] text-purple-500">{otherOrders.length} ped.</p>
                                                </div>
                                                <p className="text-sm font-bold text-purple-800">$ {fmt(otherValue)}</p>
                                                {otherPaid < otherValue && <p className="text-[10px] text-purple-500">Pagado: $ {fmt(otherPaid)}</p>}
                                            </>
                                        ) : (
                                            <>
                                                <p className={`text-[10px] font-bold ${totalPending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                    {totalPending > 0 ? '⚠ Pendiente' : '✅ Recaudado'}
                                                </p>
                                                <p className={`text-sm font-black ${totalPending > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                                    $ {fmt(totalPending > 0 ? totalPending : totalPaid)}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {/* Cash box - dark bar */}
                                <div className="bg-slate-800 rounded-lg px-4 py-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-slate-300 uppercase font-bold">💰 Caja Ruta</span>
                                        {parseFloat(settlement.cash_difference) !== 0 && (
                                            <span className="text-[10px] text-red-400 font-bold">⚠ Dif vs Declarado: $ {fmt(settlement.cash_difference)}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-6 mt-1">
                                        <div>
                                            <p className="text-[9px] text-green-400">Neto en Caja</p>
                                            <p className="text-lg font-black text-green-400">$ {fmt(settlement.received_cash)}</p>
                                        </div>
                                        {parseFloat(settlement.total_expenses || 0) > 0 && (
                                            <div>
                                                <p className="text-[9px] text-slate-500">Gastos reportados</p>
                                                <p className="text-sm font-bold text-orange-400/70">$ {fmt(settlement.total_expenses)}</p>
                                                <p className="text-[8px] text-slate-600">ya descontados del efectivo</p>
                                            </div>
                                        )}
                                        {parseFloat(settlement.additional_expense_amount) > 0 && (
                                            <div>
                                                <p className="text-[9px] text-slate-500">{settlement.additional_expense_desc || 'Gasto adicional'}</p>
                                                <p className="text-sm font-bold text-orange-400/70">$ {fmt(settlement.additional_expense_amount)}</p>
                                                <p className="text-[8px] text-slate-600">ya descontado</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Pending + collected */}
                                {otherOrders.length > 0 && (
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <div className={`rounded-lg px-3 py-2 ${totalPending > 0 ? 'bg-red-50 border border-red-300' : 'bg-emerald-50 border border-emerald-200'}`}>
                                            <p className={`text-[10px] font-bold ${totalPending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {totalPending > 0 ? '⚠ Pendiente' : '✅ Recaudado'}
                                            </p>
                                            <p className={`text-sm font-black ${totalPending > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                                $ {fmt(totalPending > 0 ? totalPending : totalPaid)}
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                                            <p className="text-[10px] text-emerald-600 font-bold">Total Recaudado</p>
                                            <p className="text-sm font-bold text-emerald-700">$ {fmt(totalPaid)} <span className="text-[10px] text-gray-400">({totalValue > 0 ? Math.round((totalPaid / totalValue) * 100) : 0}%)</span></p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* 📋 Detalle de Gastos Declarados */}
                    {(() => {
                        const hasRouteExpenses = settledExpenses && settledExpenses.length > 0;
                        const hasAdditionalExpense = parseFloat(settlement.additional_expense_amount) > 0;
                        if (!hasRouteExpenses && !hasAdditionalExpense) return null;

                        const expenseTypeLabels = {
                            fuel: '⛽ Combustible',
                            parking: '🅿️ Parqueadero',
                            toll: '🛣️ Peaje',
                            extra_stop: '📍 Parada Extra',
                            food: '🍽️ Alimentación',
                            maintenance: '🔧 Mantenimiento',
                            other: '📋 Otro'
                        };

                        return (
                            <div className="px-5 py-3 border-t border-gray-100">
                                <h3 className="font-bold text-gray-800 mb-2 text-sm flex items-center gap-2">
                                    📋 Detalle de Gastos Declarados
                                </h3>
                                <div className="space-y-1.5">
                                    {/* Route expenses from messenger */}
                                    {hasRouteExpenses && settledExpenses.map(exp => (
                                        <div key={exp.id} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${
                                            exp.approved === 1 ? 'bg-green-50 border-green-200' :
                                            exp.approved === 0 ? 'bg-red-50 border-red-200' :
                                            'bg-yellow-50 border-yellow-200'
                                        }`}>
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="font-bold text-gray-700 whitespace-nowrap">
                                                    {expenseTypeLabels[exp.expense_type] || exp.expense_type}
                                                </span>
                                                {exp.description && (
                                                    <span className="text-gray-500 truncate">— {exp.description}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 ml-2">
                                                <span className="font-black text-gray-800">$ {Number(exp.amount || 0).toLocaleString()}</span>
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                                    exp.approved === 1 ? 'bg-green-200 text-green-800' :
                                                    exp.approved === 0 ? 'bg-red-200 text-red-800' :
                                                    'bg-yellow-200 text-yellow-800'
                                                }`}>
                                                    {exp.approved === 1 ? '✓ Aprobado' : exp.approved === 0 ? '✗ Rechazado' : '⏳ Pendiente'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Rejection reasons */}
                                    {hasRouteExpenses && settledExpenses.filter(e => e.approved === 0 && e.rejection_reason).map(exp => (
                                        <div key={`rej-${exp.id}`} className="ml-4 text-[10px] text-red-600 font-medium">
                                            ↳ Rechazado: {exp.rejection_reason}
                                        </div>
                                    ))}

                                    {/* Additional expense from cajera */}
                                    {hasAdditionalExpense && (
                                        <div className="flex items-center justify-between px-3 py-2 rounded-lg border bg-orange-50 border-orange-200 text-xs">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="font-bold text-orange-700 whitespace-nowrap">💰 Gasto Adicional (Cajera)</span>
                                                {settlement.additional_expense_desc && (
                                                    <span className="text-gray-500 truncate">— {settlement.additional_expense_desc}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 ml-2">
                                                <span className="font-black text-gray-800">$ {Number(settlement.additional_expense_amount).toLocaleString()}</span>
                                                {settlement.additional_expense_receipt && (
                                                    <a
                                                        href={`/api/${settlement.additional_expense_receipt}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        📎 Soporte
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Total */}
                                    {(hasRouteExpenses || hasAdditionalExpense) && (
                                        <div className="flex items-center justify-between px-3 py-1.5 border-t border-dashed border-gray-300 text-xs mt-1">
                                            <span className="font-bold text-gray-600 uppercase text-[10px]">Total Gastos Declarados:</span>
                                            <span className="font-black text-gray-900">
                                                $ {Number(
                                                    (settledExpenses || []).reduce((s, e) => s + parseFloat(e.amount || 0), 0) +
                                                    parseFloat(settlement.additional_expense_amount || 0)
                                                ).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* 📊 Route Profitability Analysis */}
                    {(() => {
                        // ── SMLV 2026 — Costo real empleador ──
                        // Salario base: $1,423,500/mes  →  /30 = $47,450/día
                        // Aux. transporte: $200,000/mes  →  /30 = $6,667/día
                        // Factor carga prestacional: 51.68%
                        //   SS: Salud 8.5% + Pensión 12% + ARL 4.35% = 24.85%
                        //   Parafiscales: Caja 4%
                        //   Prestaciones: Prima 8.33% + Cesantías 8.33% + Int.Cesantías 1% + Vacaciones 4.17% = 21.83%
                        //   Total: 24.85 + 4 + 21.83 ≈ 50.68%  (redondeado 51%)
                        const FACTOR_PRESTACIONAL = 1.51;
                        const MONTHLY_SALARY = 1423500;
                        const MONTHLY_AUX = 200000;
                        const DAILY_FULL_COST = Math.round(((MONTHLY_SALARY + MONTHLY_AUX) * FACTOR_PRESTACIONAL) / 30);
                        // = (1,623,500 * 1.51) / 30 ≈ $81,718/día
                        const STOP_BONUS_GROSS = 2500;
                        const STOP_COST = Math.round(STOP_BONUS_GROSS * FACTOR_PRESTACIONAL); // ≈ $3,775

                        const deliveryIncome = Number(deliveryFeeStats?.total_delivery_fees_net || 0);
                        const ordersWithFee = Number(deliveryFeeStats?.orders_with_delivery_fee || 0);
                        const stops = Number(totalStops || 0);

                        // Costs
                        const fuelExpenses = (settledExpenses || []).filter(e => e.expense_type === 'fuel').reduce((s, e) => s + Number(e.amount || 0), 0);
                        const otherExpenses = (settledExpenses || []).filter(e => e.expense_type !== 'fuel').reduce((s, e) => s + Number(e.amount || 0), 0);
                        const totalRouteExpenses = fuelExpenses + otherExpenses;
                        const stopCommissions = stops * STOP_COST;
                        const driverTotalPay = DAILY_FULL_COST + stopCommissions;
                        const totalCosts = totalRouteExpenses + driverTotalPay;

                        // Result
                        const netProfit = deliveryIncome - totalCosts;
                        const isProfit = netProfit >= 0;
                        const avgPerStop = stops > 0 ? Math.round(deliveryIncome / stops) : 0;
                        const costPerStop = stops > 0 ? Math.round(totalCosts / stops) : 0;

                        return (
                            <div className="px-5 py-3 border-t border-gray-100">
                                <h3 className="font-bold text-gray-800 mb-2 text-sm flex items-center gap-2">
                                    📊 Rentabilidad de Ruta
                                </h3>
                                <div className="grid grid-cols-3 gap-2">
                                    {/* Income */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                        <p className="text-[10px] text-blue-600 font-bold uppercase">🚚 Ingresos Domicilios</p>
                                        <p className="text-sm font-black text-blue-800">$ {fmt(deliveryIncome)}</p>
                                        <p className="text-[9px] text-blue-500">{ordersWithFee} cobros • Sin IVA</p>
                                        {stops > 0 && <p className="text-[9px] text-blue-400">Prom: $ {fmt(avgPerStop)}/parada</p>}
                                    </div>
                                    {/* Costs */}
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                                        <p className="text-[10px] text-orange-600 font-bold uppercase">💸 Costos Ruta</p>
                                        <p className="text-sm font-black text-orange-800">$ {fmt(totalCosts)}</p>
                                        <div className="text-[9px] text-orange-500 space-y-0.5 mt-0.5">
                                            <div className="flex justify-between"><span>👤 Salario + prest.:</span><span>$ {fmt(DAILY_FULL_COST)}</span></div>
                                            <div className="flex justify-between pl-2 text-orange-400"><span>SMLV+Aux+SS+Prest.</span><span className="text-[8px]">×{FACTOR_PRESTACIONAL}</span></div>
                                            <div className="flex justify-between"><span>📍 Comisiones:</span><span>$ {fmt(stopCommissions)}</span></div>
                                            <div className="flex justify-between pl-2 text-orange-400"><span>{stops}×${fmt(STOP_BONUS_GROSS)}×{FACTOR_PRESTACIONAL}</span></div>
                                            {fuelExpenses > 0 && <div className="flex justify-between"><span>⛽ Combustible:</span><span>$ {fmt(fuelExpenses)}</span></div>}
                                            {otherExpenses > 0 && <div className="flex justify-between"><span>📋 Otros gastos:</span><span>$ {fmt(otherExpenses)}</span></div>}
                                        </div>
                                        {stops > 0 && <p className="text-[9px] text-orange-400 mt-0.5">Costo: $ {fmt(costPerStop)}/parada</p>}
                                    </div>
                                    {/* Result */}
                                    <div className={`rounded-lg px-3 py-2 border ${isProfit ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                                        <p className={`text-[10px] font-bold uppercase ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {isProfit ? '✅ Ganancia' : '❌ Pérdida'}
                                        </p>
                                        <p className={`text-lg font-black ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
                                            {isProfit ? '' : '-'}$ {fmt(Math.abs(netProfit))}
                                        </p>
                                        <p className={`text-[9px] ${isProfit ? 'text-emerald-500' : 'text-red-500'}`}>
                                            {deliveryIncome > 0 ? `Margen: ${Math.round((netProfit / deliveryIncome) * 100)}%` : 'Sin ingresos domicilio'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* All Orders Table - Compact */}
                    <div className="px-5 py-3">
                        <h3 className="font-bold text-gray-800 mb-2 text-sm flex items-center gap-2">
                            <ClipboardList size={14} /> Todos los Pedidos ({orders?.length || 0})
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 uppercase text-[9px]">
                                        <th className="px-1.5 py-1 text-left w-6">#</th>
                                        <th className="px-1.5 py-1 text-left">Orden</th>
                                        <th className="px-1.5 py-1 text-left">Cliente</th>
                                        <th className="px-1.5 py-1 text-left">Pago</th>
                                        <th className="px-1.5 py-1 text-right">Valor</th>
                                        <th className="px-1.5 py-1 text-right">Pagado</th>
                                        <th className="px-1.5 py-1 text-right">Saldo</th>
                                        <th className="px-1.5 py-1 text-center w-8"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {orders?.map((o, i) => {
                                        const balanceDue = parseFloat(o.balance_due || 0);
                                        const orderValue = parseFloat(o.total_amount || 0);
                                        const paidAmount = parseFloat(o.paid_amount || 0);
                                        return (
                                            <tr key={o.id} className={`${o.delivery_status === 'failed' ? 'bg-red-50' : balanceDue > 0 ? 'bg-amber-50' : ''}`}>
                                                <td className="px-1.5 py-1 text-gray-400">{i + 1}</td>
                                                <td className="px-1.5 py-1 font-bold text-gray-800">{o.order_number}</td>
                                                <td className="px-1.5 py-1 text-gray-600 max-w-[120px] truncate">{o.customer_name}</td>
                                                <td className="px-1.5 py-1">
                                                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold ${o.payment_method === 'efectivo' || o.payment_method === 'contraentrega'
                                                        ? 'bg-green-100 text-green-700'
                                                        : o.payment_method === 'transferencia'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-purple-100 text-purple-700'
                                                        }`}>
                                                        {o.payment_method}
                                                    </span>
                                                </td>
                                                <td className="px-1.5 py-1 text-right font-medium">$ {fmt(orderValue)}</td>
                                                <td className="px-1.5 py-1 text-right font-medium text-green-700">$ {fmt(paidAmount)}</td>
                                                <td className={`px-1.5 py-1 text-right font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {balanceDue > 0 ? `$ ${fmt(balanceDue)}` : '✓'}
                                                </td>
                                                <td className="px-1.5 py-1 text-center">
                                                    {o.delivery_status === 'failed' && <span className="text-red-600 text-[9px] font-bold">✗</span>}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 font-bold text-[11px]">
                                        <td colSpan={4} className="px-1.5 py-1.5 text-right uppercase text-gray-500 text-[9px]">Totales</td>
                                        <td className="px-1.5 py-1.5 text-right">$ {fmt(orders?.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))}</td>
                                        <td className="px-1.5 py-1.5 text-right text-green-700">$ {fmt(orders?.reduce((s, o) => s + parseFloat(o.paid_amount || 0), 0))}</td>
                                        <td className={`px-1.5 py-1.5 text-right ${orders?.reduce((s, o) => s + parseFloat(o.balance_due || 0), 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            $ {fmt(orders?.reduce((s, o) => s + parseFloat(o.balance_due || 0), 0))}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Payment Proofs - Collapsible */}
                    {paymentProofs && paymentProofs.length > 0 && (
                        <div className="px-5 py-2 border-t border-gray-100">
                            <details className="text-xs">
                                <summary className="cursor-pointer text-purple-600 font-bold hover:text-purple-800">
                                    📎 {paymentProofs.length} soporte(s) de transferencia — click para ver
                                </summary>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                    {paymentProofs.map(proof => {
                                        const imgUrl = proof.photo_path?.startsWith('http') ? proof.photo_path : `/api${proof.photo_path}`;
                                        return (
                                            <a key={proof.id} href={imgUrl} target="_blank" rel="noreferrer" className="block border rounded overflow-hidden hover:shadow transition-shadow">
                                                <img src={imgUrl} alt={`Soporte ${proof.order_number}`} className="w-full h-20 object-cover bg-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
                                                <div className="px-2 py-1 bg-gray-50 text-[10px] text-gray-600 truncate">{proof.order_number} • $ {fmt(proof.total_amount)}</div>
                                            </a>
                                        );
                                    })}
                                </div>
                            </details>
                        </div>
                    )}
                </div>
            </div >
        );
    };

    // --- LIST VIEW ---
    if (!selectedRoute) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Liquidación de Rutas</h1>
                    <div className="flex gap-2 mt-3">
                        <button
                            onClick={() => handleTabChange('pending')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeTab === 'pending'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                                }`}
                        >
                            Pendientes {routes.length > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{routes.length}</span>}
                        </button>
                        <button
                            onClick={() => handleTabChange('history')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeTab === 'history'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                                }`}
                        >
                            Historial
                        </button>
                    </div>
                </div>

                {activeTab === 'history' ? renderHistoryTab() : (
                    <>
                        {routes.length === 0 ? (
                            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="text-gray-300 w-8 h-8" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800">No hay rutas pendientes</h3>
                                <p className="text-gray-500 max-w-xs mx-auto mt-2">
                                    Todas las rutas han sido liquidadas correctamente.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {routes.map(route => (
                                    <div
                                        key={route.id}
                                        onClick={() => fetchRouteDetails(route)}
                                        className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer group"
                                    >
                                        <div className="p-5">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                                        #{route.route_number || route.id}
                                                    </span>
                                                    <h3 className="text-lg font-bold text-gray-900 mt-2">{route.messenger_name}</h3>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-400">Cerrada el</p>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        {new Date(route.closed_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-3 pt-4 border-t border-gray-50">
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-500">Pedidos:</span>
                                                    <span className="font-bold text-gray-800">{route.total_orders}</span>
                                                </div>
                                                {route.additional_stops_count > 0 && (
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-gray-500 flex items-center gap-1"><MapPin size={12} className="text-indigo-500" /> Paradas Adic.:</span>
                                                        <span className="font-bold text-indigo-600">{route.additional_stops_count}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-500">Recaudo Esperado:</span>
                                                    <span className="font-bold text-blue-600">{formatCurrency(route.expected_cash)}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-500">Recaudo Declarado:</span>
                                                    <span className="font-bold text-green-600">{formatCurrency(route.declared_cash)}</span>
                                                </div>
                                            </div>

                                            <div className="mt-6 flex items-center justify-center text-blue-600 font-bold text-sm bg-blue-50 group-hover:bg-blue-100 py-2 rounded-lg transition-colors">
                                                Liquidar Ruta <ChevronRight size={16} className="ml-1" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    // --- DETAIL VIEW ---
    const totalRejected = rejectedExpenses.reduce((sum, r) => {
        const exp = expenses.find(e => e.id === r.expenseId);
        return sum + (exp ? parseFloat(exp.amount) : 0);
    }, 0);

    // Calculate INT cash globally for use in multiple panels
    const cashMethodsForInt = ['efectivo', 'efectivo_ruta_domicilio', 'contraentrega'];
    const intCashTotal = orders.reduce((sum, order) => {
        if (order.delivery_status === 'failed') return sum;
        if (cashMethodsForInt.includes(order.payment_method) && parseFloat(order.internal_invoice_value || 0) > 0) {
            return sum + parseFloat(order.internal_invoice_value || 0);
        }
        return sum;
    }, 0);
    const intOrderCount = orders.filter(o => 
        o.delivery_status !== 'failed' && 
        cashMethodsForInt.includes(o.payment_method) && 
        parseFloat(o.internal_invoice_value || 0) > 0
    ).length;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <button
                onClick={() => setSelectedRoute(null)}
                className="mb-6 text-gray-500 hover:text-blue-600 font-medium flex items-center gap-1 transition-colors"
                disabled={isSettling}
            >
                ← Volver al listado
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Summary & Expenses */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Header Info */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                                    <Truck size={30} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">{selectedRoute.messenger_name}</h2>
                                    <p className="text-sm text-gray-500">Ruta #{selectedRoute.route_number || selectedRoute.id} • {new Date(selectedRoute.route_date).toLocaleDateString()}</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
                                    <p className="text-[10px] text-blue-600 uppercase font-bold">Pedidos</p>
                                    <p className="text-lg font-bold text-blue-700">{selectedRoute.total_orders}</p>
                                </div>
                                {additionalStops.length > 0 && (
                                    <div className="bg-indigo-50 px-4 py-2 rounded-lg border border-indigo-100">
                                        <p className="text-[10px] text-indigo-600 uppercase font-bold">Paradas Adic.</p>
                                        <p className="text-lg font-bold text-indigo-700">{additionalStops.length}</p>
                                    </div>
                                )}
                                <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-100">
                                    <p className="text-[10px] text-green-600 uppercase font-bold">Efectivo Sistema</p>
                                    <p className="text-lg font-bold text-green-700">{formatCurrency(selectedRoute.expected_cash)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Resumen de Medios de Pago */}
                    {Object.keys(paymentSummary).length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            <h3 className="text-xs font-black text-gray-400 uppercase mb-4 flex items-center gap-2">
                                <DollarSign size={14} className="text-blue-500" /> Resumen de Medios de Pago en Ruta
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {Object.entries(paymentSummary).map(([method, total]) => {
                                    const methods = {
                                        efectivo: { label: 'Efectivo', icon: Banknote, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
                                        transferencia: { label: 'Transferencia', icon: Smartphone, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
                                        pago_electronico: { label: 'P. Electrónico', icon: CreditCard, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100' },
                                        tarjeta_credito: { label: 'T. Crédito', icon: CreditCard, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100' }
                                    };
                                    const cfg = methods[method] || { label: method, icon: Wallet, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-100' };
                                    const Icon = cfg.icon;
                                    return (
                                        <div key={method} className={`${cfg.bg} ${cfg.border} p-3 rounded-xl border-l-4 shadow-sm group hover:shadow-md transition-all flex flex-col justify-between`}>
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <div className={`${cfg.color} p-1.5 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform`}>
                                                        <Icon size={16} />
                                                    </div>
                                                    <p className="text-[10px] font-black text-gray-500 uppercase truncate">{cfg.label}</p>
                                                </div>
                                                <p className={`text-lg font-black ${cfg.color}`}>
                                                    {formatCurrency(method === 'efectivo' ? total + intCashTotal : total)}
                                                </p>
                                            </div>
                                            {method === 'efectivo' && intCashTotal > 0 && (
                                                <div className="mt-2 text-[9px] border-t border-green-200 pt-1">
                                                    <div className="flex justify-between text-green-700">
                                                        <span>Siigo:</span>
                                                        <span className="font-bold">{formatCurrency(total)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-indigo-600">
                                                        <span>INT:</span>
                                                        <span className="font-bold">+{formatCurrency(intCashTotal)}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Total across all methods */}
                                <div className="bg-gray-800 p-3 rounded-xl border-l-4 border-gray-600 shadow-sm col-span-full md:col-span-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <div className="text-white p-1.5 bg-gray-700 rounded-lg shadow-sm">
                                            <Wallet size={16} />
                                        </div>
                                        <p className="text-[10px] font-black text-gray-300 uppercase">Total Pagos</p>
                                    </div>
                                    <p className="text-lg font-black text-white" title="Incluye pagos asentados + reportes pendientes del mensajero">
                                        {formatCurrency(
                                            (Object.values(paymentSummary).reduce((s, v) => s + v, 0)) + 
                                            (orders || []).reduce((sum, o) => {
                                                const rep = parseFloat(o.reporter_data?.cash || 0);
                                                const set = (parseFloat(o.total_paid_amount || 0) + parseFloat(o.children_paid_amount || 0)) || 0;
                                                return sum + (rep > set ? (rep - set) : 0);
                                            }, 0)
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Orders Table Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-gray-700 flex items-center gap-2">
                                <ClipboardList size={18} className="text-blue-600" />
                                Resumen de Pedidos ({orders.length})
                            </h3>
                        </div>
                        <div>
                            <table className="w-full text-[11px] text-left table-fixed">
                                <colgroup>
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '13%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '16%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '7%' }} />
                                </colgroup>
                                <thead className="bg-gray-50 text-gray-500 border-b">
                                    <tr>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px]">Orden</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px]">Cliente</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px]">Pago</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">Domic.</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">INT</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">Pedido</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">Adi+Ped</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">Total</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right">Soportes/RCs</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-right text-red-600">Saldo</th>
                                        <th className="px-1 py-1 font-bold uppercase text-[9px] text-center">Acción</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {orders.map(order => {
                                        const adicVal = parseFloat(order.internal_invoice_value || 0);
                                        const siigoVal = parseFloat(order.invoice_value || 0) - parseFloat(order.do_value || 0);
                                        const adiPlusPed = adicVal + siigoVal;
                                        const totalGral = adiPlusPed + parseFloat(order.do_value || 0);
                                        const isDelivered = order.messenger_status === 'delivered' || order.status === 'entregado';
                                        const isFailed = order.delivery_status === 'failed' || order.messenger_status === 'failed' || order.status === 'fallido';
                                        const isUndelivered = !isDelivered && !isFailed;
                                        const isAlreadyReturned = order.status === 'no_entregado' || order.status === 'gestion_especial'
                                            || ['no_entregado', 'gestion_especial', 'listo_para_entrega', 'empacando', 'pendiente_por_facturacion'].includes(order.order_status)
                                            // 🛡️ ROUTE ISOLATION: If the order failed on this route but was already delivered (re-assigned to another route), disable return
                                            || (isFailed && order.status === 'entregado')
                                            || (isFailed && (order.notes || '').includes('[Retorno]'));

                                        const rowBg = isFailed
                                            ? 'bg-red-50/60 hover:bg-red-100 border-l-4 border-l-red-400'
                                            : isUndelivered
                                                ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400'
                                                : 'hover:bg-gray-50';

                                        return (
                                            <tr key={order.id} className={`${rowBg} transition-colors`}>
                                                <td className="px-1 py-1">
                                                    <div className={`font-bold text-gray-900 text-[10px] ${isFailed ? 'line-through text-gray-400' : ''}`}>{order.order_number}</div>
                                                    {isFailed ? (
                                                        <span className="px-1 rounded text-[8px] font-black uppercase bg-red-200 text-red-800">
                                                            ↩ Retornado
                                                        </span>
                                                    ) : (
                                                        <span className={`px-1 rounded text-[8px] font-black uppercase ${order.messenger_status === 'delivered' ? 'bg-green-100 text-green-700' :
                                                            order.messenger_status === 'failed' ? 'bg-red-100 text-red-700' :
                                                                'bg-blue-100 text-blue-700'
                                                            }`}>
                                                            {order.messenger_status === 'delivered' ? 'Entr' :
                                                                order.messenger_status === 'failed' ? 'Fail' : 'Ruta'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-1 py-1">
                                                    <div className="text-gray-700 font-medium truncate" title={order.customer_name}>{order.customer_name}</div>
                                                </td>
                                                <td className="px-1 py-1">
                                                    <div className={`px-1 py-0.5 rounded text-[9px] font-black w-fit border ${order.payment_method === 'efectivo' ? 'bg-green-50 text-green-700 border-green-200' :
                                                        order.payment_method === 'transferencia' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                            'bg-gray-50 text-gray-700 border-gray-200'
                                                        }`}>
                                                        {order.payment_method === 'transferencia' ? 'TRANSF' : order.payment_method?.toUpperCase()}
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 text-right font-medium text-green-600">
                                                    {order.do_base_value > 0 ? formatCurrency(order.do_base_value) : (order.do_value > 0 ? formatCurrency(order.do_value) : '-')}
                                                </td>
                                                <td className="px-1 py-1 text-right text-gray-400">
                                                    {adicVal > 0 ? (
                                                        <div>
                                                            <div>{formatCurrency(adicVal)}</div>
                                                            {order.internal_invoice_numbers && (
                                                                <div className="text-[8px] text-indigo-500 font-bold truncate" title={order.internal_invoice_numbers}>
                                                                    {order.internal_invoice_numbers}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-1 py-1 text-right text-gray-600">
                                                    {formatCurrency(siigoVal)}
                                                </td>
                                                <td className="px-1 py-1 text-right font-medium text-gray-600">
                                                    {formatCurrency(adiPlusPed)}
                                                </td>
                                                <td className="px-1 py-1 text-right font-bold text-gray-900 border-l border-gray-50 bg-gray-50/30">
                                                    {formatCurrency(totalGral)}
                                                </td>
                                                <td className="px-1 py-1 text-right">
                                                    <div className="flex flex-col items-end gap-1">
                                                        {/* Datos Reportados por el Mensajero (No Asentados) */}
                                                        {order.reporter_data && (order.reporter_data.cash > 0 || order.reporter_data.transfer > 0 || order.reporter_data.payment_method) && (
                                                            <div className="flex flex-wrap justify-end gap-1 border-b border-gray-100 pb-1 mb-0.5 w-full">
                                                                <span className="w-full text-[9px] font-black uppercase text-gray-400 text-right">Reporte Mensajero:</span>
                                                                {order.reporter_data.payment_method && (
                                                                    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-black border ${order.reporter_data.payment_method === 'efectivo' || order.reporter_data.payment_method === 'efectivo_ruta_domicilio'
                                                                        ? 'bg-green-50 text-green-700 border-green-200'
                                                                        : order.reporter_data.payment_method === 'transferencia'
                                                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                                            : order.reporter_data.payment_method === 'mixto'
                                                                                ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                                                : 'bg-gray-50 text-gray-700 border-gray-200'
                                                                        }`}>
                                                                        {order.reporter_data.payment_method === 'efectivo' || order.reporter_data.payment_method === 'efectivo_ruta_domicilio'
                                                                            ? <><Banknote size={10} /> EFECTIVO</>
                                                                            : order.reporter_data.payment_method === 'transferencia'
                                                                                ? <><Smartphone size={10} /> TRANSFERENCIA</>
                                                                                : order.reporter_data.payment_method === 'tarjeta'
                                                                                    ? <><CreditCard size={10} /> TARJETA</>
                                                                                    : order.reporter_data.payment_method === 'mixto'
                                                                                        ? <><Wallet size={10} /> MIXTO</>
                                                                                        : <><Wallet size={10} /> {order.reporter_data.payment_method?.toUpperCase()}</>
                                                                        }
                                                                    </div>
                                                                )}
                                                                {order.reporter_data.cash > 0 && (
                                                                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-50/50 text-green-600 border border-green-100 text-[10px] font-bold">
                                                                        <Banknote size={10} /> {formatCurrency(order.reporter_data.cash)}
                                                                    </div>
                                                                )}
                                                                {order.reporter_data.transfer > 0 && (
                                                                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-blue-50/50 text-blue-600 border border-blue-100 text-[10px] font-bold">
                                                                        <Smartphone size={10} /> {formatCurrency(order.reporter_data.transfer)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Transacciones Detalladas (Asentadas) */}
                                                        {order.transactions && order.transactions.length > 0 && (
                                                            <div className="flex flex-wrap justify-end gap-1">
                                                                <span className="w-full text-[9px] font-black uppercase text-blue-400 text-right">Cobros Asentados:</span>
                                                                {order.transactions.map((tx, idx) => {
                                                                    const isCash = tx.method === 'efectivo';
                                                                    const isTrans = tx.method === 'transferencia';
                                                                    return (
                                                                        <div key={idx} className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${isCash ? 'bg-green-50 text-green-700 border-green-200' : isTrans ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                                                            {isCash ? <Banknote size={12} /> : isTrans ? <Smartphone size={12} /> : <Wallet size={12} />}
                                                                            {formatCurrency(tx.amount)}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {/* Evidencias de Pago (Fotos) */}
                                                        {order.evidences && order.evidences.length > 0 && (() => {
                                                            const paymentProofs = order.evidences.filter(ev => ev.type === 'payment_proof');
                                                            const deliveryPhotos = order.evidences.filter(ev => ev.type === 'messenger_report');
                                                            const otherEvidences = order.evidences.filter(ev => ev.type !== 'payment_proof' && ev.type !== 'messenger_report');
                                                            return (
                                                                <>
                                                                    {/* Comprobantes de Transferencia */}
                                                                    {paymentProofs.length > 0 && (
                                                                        <div className="flex flex-wrap justify-end gap-1">
                                                                            <span className="w-full text-[9px] font-black uppercase text-emerald-500 text-right">📎 Soporte Transferencia:</span>
                                                                            {paymentProofs.map((ev, idx) => (
                                                                                <button
                                                                                    key={`pp-${idx}`}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        const url = ev.path?.startsWith('http') ? ev.path : ev.path?.includes('uploads/') ? `/api/${ev.path}` : `/api/uploads/evidence/delivery/${ev.path}`;
                                                                                        setPreviewImage(url);
                                                                                    }}
                                                                                    className="p-1.5 border rounded-md transition-colors flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 hover:shadow-sm"
                                                                                    title="Comprobante de Transferencia"
                                                                                >
                                                                                    <Smartphone size={12} />
                                                                                    <span className="text-[9px] uppercase font-bold">Transf</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {/* Fotos de Entrega del Mensajero */}
                                                                    {deliveryPhotos.length > 0 && (
                                                                        <div className="flex flex-wrap justify-end gap-1">
                                                                            {deliveryPhotos.map((ev, idx) => (
                                                                                <button
                                                                                    key={`dr-${idx}`}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        const url = ev.path?.startsWith('http') ? ev.path : ev.path?.includes('uploads/') ? `/api/${ev.path}` : `/api/uploads/evidence/delivery/${ev.path}`;
                                                                                        setPreviewImage(url);
                                                                                    }}
                                                                                    className="p-1 border rounded-md transition-colors flex items-center gap-1 bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                                                                                    title="Foto Entrega Mensajero"
                                                                                >
                                                                                    <Camera size={12} />
                                                                                    <span className="text-[9px] uppercase font-bold">Rep</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {/* Otras Evidencias (pagos asentados, multi-rc, etc.) */}
                                                                    {otherEvidences.length > 0 && (
                                                                        <div className="flex flex-wrap justify-end gap-1">
                                                                            {otherEvidences.map((ev, idx) => (
                                                                                <button
                                                                                    key={`ot-${idx}`}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        const url = ev.path?.startsWith('http') ? ev.path : ev.path?.includes('uploads/') ? `/api/${ev.path}` : `/api/uploads/evidence/payments/${ev.path}`;
                                                                                        setPreviewImage(url);
                                                                                    }}
                                                                                    className="p-1 border rounded-md transition-colors flex items-center gap-1 bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100"
                                                                                    title="Soporte Pago Asentado"
                                                                                >
                                                                                    <Camera size={12} />
                                                                                    <span className="text-[9px] uppercase font-bold">Pag</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}

                                                        {/* Recibos de Caja (Siigo) */}
                                                        {order.rcs && order.rcs.length > 0 ? (
                                                            <div className="flex flex-wrap justify-end gap-1">
                                                                {order.rcs.map((rc, idx) => (
                                                                    <span key={idx} className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md font-bold">
                                                                        <FileText size={12} />
                                                                        {rc.name || 'RC'} {rc.value ? `(${formatCurrency(rc.value)})` : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            (!order.transactions || order.transactions.length === 0) && <span className="text-gray-300">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 text-right font-black">
                                                    {order.balance_due > 0 ? (
                                                        <span className="text-red-700">{formatCurrency(order.balance_due)}</span>
                                                    ) : (
                                                        <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">PAGADO</span>
                                                    )}
                                                </td>
                                                <td className="px-1 py-1 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        {order.balance_due > 0 && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedPaymentOrder({
                                                                        ...order,
                                                                        total_amount: order.invoice_value || order.total_value || 0,
                                                                        internal_orders_total: order.internal_invoice_value || 0,
                                                                        paid_amount: (parseFloat(order.total_paid_amount || 0) + parseFloat(order.children_paid_amount || 0)) || order.paid_amount || 0
                                                                    });
                                                                    setShowPaymentModal(true);
                                                                }}
                                                                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-900 border border-emerald-200 transition-all cursor-pointer"
                                                                title="Aplicar Pago"
                                                            >
                                                                <DollarSign className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                        {(isUndelivered || isFailed) && !isAlreadyReturned && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleReturnToPackaging(order);
                                                                }}
                                                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all cursor-pointer ${isFailed
                                                                    ? 'bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-900 border-red-300'
                                                                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-900 border-amber-300'
                                                                    }`}
                                                                title="Retornar a Empaque"
                                                            >
                                                                <RotateCcw className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                    {(() => {
                                        const totals = orders.reduce((acc, order) => {
                                            const adicVal = parseFloat(order.internal_invoice_value || 0);
                                            const siigoVal = parseFloat(order.invoice_value || 0) - parseFloat(order.do_value || 0);
                                            const adiPlusPed = adicVal + siigoVal;
                                            const totalGral = adiPlusPed + parseFloat(order.do_value || 0);
                                            acc.domicilio += parseFloat(order.do_value || 0);
                                            acc.adi += adicVal;
                                            acc.vPedido += siigoVal;
                                            acc.adiPed += adiPlusPed;
                                            acc.total += totalGral;
                                            acc.saldo += parseFloat(order.balance_due || 0);
                                            return acc;
                                        }, { domicilio: 0, adi: 0, vPedido: 0, adiPed: 0, total: 0, saldo: 0 });
                                        return (
                                            <tr className="text-xs font-black">
                                                <td className="px-1 py-1.5" colSpan="3">TOTALES</td>
                                                <td className="px-1 py-1.5 text-right text-green-700">{totals.domicilio > 0 ? formatCurrency(totals.domicilio) : '-'}</td>
                                                <td className="px-1 py-1.5 text-right text-gray-600">{totals.adi > 0 ? formatCurrency(totals.adi) : '-'}</td>
                                                <td className="px-1 py-1.5 text-right text-gray-700">{formatCurrency(totals.vPedido)}</td>
                                                <td className="px-1 py-1.5 text-right text-gray-700">{formatCurrency(totals.adiPed)}</td>
                                                <td className="px-1 py-1.5 text-right text-gray-900 bg-gray-200">{formatCurrency(totals.total)}</td>
                                                <td className="px-1 py-1.5 text-right"></td>
                                                <td className="px-1 py-1.5 text-right text-red-700">{totals.saldo > 0 ? formatCurrency(totals.saldo) : <span className="text-green-700">$0</span>}</td>
                                                <td className="px-1 py-1.5"></td>
                                            </tr>
                                        );
                                    })()}
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Additional Stops Section */}
                    {additionalStops.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-4 border-b bg-indigo-50 flex justify-between items-center">
                                <h3 className="font-bold text-indigo-700 flex items-center gap-2">
                                    <MapPin size={18} className="text-indigo-600" />
                                    Paradas Adicionales ({additionalStops.length})
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {additionalStops.map(stop => {
                                    const typeConfig = {
                                        pickup: { label: '🔄 Recogida', color: 'text-amber-700', bg: 'bg-amber-50' },
                                        delivery: { label: '📦 Entrega', color: 'text-blue-700', bg: 'bg-blue-50' },
                                        errand: { label: '📍 Diligencia', color: 'text-indigo-700', bg: 'bg-indigo-50' },
                                        other: { label: '📍 Parada', color: 'text-gray-700', bg: 'bg-gray-50' }
                                    };
                                    const cfg = typeConfig[stop.stop_type] || typeConfig.other;
                                    const isCompleted = stop.status === 'completed';

                                    return (
                                        <div key={stop.id} className="p-4 hover:bg-gray-50 transition-colors">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${cfg.bg} ${cfg.color} border`}>
                                                            {cfg.label}
                                                        </span>
                                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                            {isCompleted ? '✓ Completada' : '◻ Pendiente'}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm font-bold text-gray-800 mt-1">{stop.description || stop.address}</p>
                                                    {stop.address && stop.description && (
                                                        <p className="text-xs text-gray-500 mt-0.5">{stop.address}</p>
                                                    )}
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                        {stop.contact_name && (
                                                            <span>👤 {stop.contact_name}</span>
                                                        )}
                                                        {stop.contact_phone && (
                                                            <span>📞 {stop.contact_phone}</span>
                                                        )}
                                                        {stop.completed_at && (
                                                            <span>🕐 {new Date(stop.completed_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</span>
                                                        )}
                                                    </div>
                                                    {stop.notes && (
                                                        <p className="text-xs text-gray-500 mt-1 italic">Nota: {stop.notes}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {stop.evidence_photo && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setPreviewImage(`/api/uploads/evidence/additional_stops/${stop.evidence_photo}`);
                                                            }}
                                                            className="p-1.5 border rounded-md transition-colors flex items-center gap-1 bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100"
                                                            title="Evidencia de Parada"
                                                        >
                                                            <Camera size={14} />
                                                            <span className="text-[9px] uppercase font-bold">Foto</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Expenses Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-gray-700 flex items-center gap-2">
                                <DollarSign size={18} className="text-yellow-600" />
                                Gastos de la Ruta ({expenses.length})
                            </h3>
                            <span className="text-xs text-gray-500 font-medium italic">
                                Total Gastos: {formatCurrency(selectedRoute.total_expenses)}
                            </span>
                        </div>

                        <div className="divide-y divide-gray-100">
                            {expenses.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 italic">No se registraron gastos en esta ruta</div>
                            ) : (
                                expenses.map(exp => {
                                    const isApproved = approvedExpenses.includes(exp.id);
                                    const rejection = rejectedExpenses.find(r => r.expenseId === exp.id);
                                    const isRejected = !!rejection;

                                    return (
                                        <div key={exp.id} className="p-4 hover:bg-gray-50 transition-colors">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`w-2 h-2 rounded-full ${isApproved ? 'bg-green-500' : isRejected ? 'bg-red-500' : 'bg-yellow-400'}`}></span>
                                                        <span className="font-bold text-gray-800 uppercase text-xs">{exp.expense_type}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-700">{exp.description || 'Sin descripción'}</p>
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <span className="text-lg font-bold text-gray-900">{formatCurrency(exp.amount)}</span>
                                                        {exp.receipt_photo && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setPreviewImage(`/api/uploads/evidence/expenses/${exp.receipt_photo}`);
                                                                }}
                                                                className="text-xs text-blue-600 flex items-center gap-1 hover:underline"
                                                            >
                                                                <ImageIcon size={14} /> Ver Recibo
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleApproveExpense(exp.id)}
                                                        className={`p-2 rounded-lg border transition-all ${isApproved ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-green-500 hover:text-green-500'}`}
                                                        title="Aprobar"
                                                    >
                                                        <Check size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const reason = prompt('Motivo del rechazo:', 'No justificado');
                                                            if (reason) handleRejectExpense(exp.id, reason);
                                                        }}
                                                        className={`p-2 rounded-lg border transition-all ${isRejected ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-red-500 hover:text-red-500'}`}
                                                        title="Rechazar"
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                            {isRejected && (
                                                <p className="mt-2 text-xs font-bold text-red-600 bg-red-50 p-2 rounded">
                                                    Rechazado: {rejection.reason}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Reconciliation Form */}
                <div className="space-y-6">
                    {/* Financial Dashboard */}
                    {(() => {
                        // Compute all summary metrics from orders
                        const totalApprovedExpenses = approvedExpenses.reduce((sum, id) => {
                            const exp = expenses.find(e => e.id === id);
                            return sum + (exp ? parseFloat(exp.amount) : 0);
                        }, 0);
                        const addlExp = parseFloat(additionalExpenseAmount) || 0;
                        const totalAllExpenses = totalApprovedExpenses + addlExp;

                        const orderTotals = orders.reduce((acc, order) => {
                            const invoiceVal = parseFloat(order.invoice_value || 0);
                            const doVal = parseFloat(order.do_value || 0);
                            const doBaseVal = parseFloat(order.do_base_value || 0);
                            const intVal = parseFloat(order.internal_invoice_value || 0);
                            acc.totalOrders += invoiceVal + intVal;
                            acc.domicilio += doVal;
                            acc.domicilioBase += doBaseVal;
                            acc.totalBalance += parseFloat(order.balance_due || 0);
                            return acc;
                        }, { totalOrders: 0, domicilio: 0, domicilioBase: 0, totalBalance: 0 });

                        const totalPagos = Object.values(paymentSummary).reduce((s, v) => s + v, 0);
                        // Cash from RC transactions (applied by cajera)
                        const cashFromRC = paymentSummary['efectivo'] || 0;
                        const transferPayments = paymentSummary['transferencia'] || 0;
                        const electronicPayments = paymentSummary['pago_electronico'] || 0;
                        const otherPayments = totalPagos - cashFromRC - transferPayments - electronicPayments;

                        // INT cash: INT orders don't generate their own RCs/OPTs.
                        // The parent's OPT/CR already includes the INT amount when paid in cash,
                        // so adding intCash would DOUBLE-COUNT. For 'contraentrega' orders that
                        // end up paying by transfer, counting INT as cash is incorrect.
                        // → cashFromRC (paymentSummary['efectivo']) IS the ground truth.
                        const intCash = 0; // Already included in parent's OPT entry

                        // Cash from orders (what the messenger actually collects in cash)
                        // total_value already includes INT children (computed by backend)
                        const cashMethods = ['efectivo', 'efectivo_ruta_domicilio', 'contraentrega'];
                        const cashFromOrders = orders.reduce((sum, order) => {
                            if (order.delivery_status === 'failed') return sum;
                            if (cashMethods.includes(order.payment_method)) {
                                return sum + parseFloat(order.total_value || order.invoice_value || 0);
                            }
                            if (order.payment_method === 'mixto' && parseFloat(order.reporter_cash || 0) > 0) {
                                return sum + parseFloat(order.reporter_cash || 0);
                            }
                            return sum;
                        }, 0);
                        const cashPayments = cashFromRC; // 🛡️ Ground truth: OPT efectivo (ONLY Siigo)
                        const cashExpectedFromOrders = cashFromRC; // Same — OPT is authoritative
                        const cashFromOrdersEstimate = cashFromOrders; // Messenger estimate (reference only)
                        const efectivoNetoEsperado = (cashFromRC + intCashTotal) - totalAllExpenses; // Net = (OPT + INT) - expenses

                        return (
                            <>
                                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                                    <div className="p-4 bg-gray-900 text-white">
                                        <h3 className="font-bold text-lg">Resumen de Liquidación</h3>
                                    </div>

                                    <div className="p-5 space-y-3">
                                        {/* Counts */}
                                        <div className="grid grid-cols-3 gap-2 pb-3 border-b">
                                            <div className="text-center">
                                                <p className="text-[10px] font-black text-gray-400 uppercase">Pedidos</p>
                                                <p className="text-lg font-black text-gray-900">{orders.length}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] font-black text-gray-400 uppercase">Paradas</p>
                                                <p className="text-lg font-black text-gray-900">{orders.length + additionalStops.length}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] font-black text-gray-400 uppercase">Adic.</p>
                                                <p className="text-lg font-black text-indigo-600">{additionalStops.length}</p>
                                            </div>
                                        </div>

                                        {/* Order values */}
                                        <div className="pb-3 border-b space-y-1.5">
                                            <p className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1"><ClipboardList size={10} /> Valor Pedidos</p>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Total Pedidos:</span>
                                                <span className="font-bold text-gray-900">{formatCurrency(orderTotals.totalOrders)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Domicilios (con IVA):</span>
                                                <span className="font-bold text-green-600">{formatCurrency(orderTotals.domicilio)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Domicilios (sin IVA):</span>
                                                <span className="font-bold text-green-700">{formatCurrency(orderTotals.domicilioBase)}</span>
                                            </div>
                                            {orderTotals.domicilio - orderTotals.domicilioBase > 0 && (
                                                <div className="flex justify-between text-xs text-gray-400">
                                                    <span>IVA Domicilios:</span>
                                                    <span>{formatCurrency(orderTotals.domicilio - orderTotals.domicilioBase)}</span>
                                                </div>
                                            )}
                                            {orderTotals.totalBalance > 0 && (
                                                <div className="mt-2 p-3 rounded-lg bg-red-50 border-2 border-red-300">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs font-black text-red-700 uppercase">⚠ Falta por Cobrar:</span>
                                                        <span className="text-lg font-black text-red-700">{formatCurrency(orderTotals.totalBalance)}</span>
                                                    </div>
                                                    <p className="text-[10px] text-red-500 mt-0.5">
                                                        {orders.filter(o => parseFloat(o.balance_due || 0) > 0).length} pedido(s) con saldo pendiente
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Payment breakdown */}
                                        <div className="pb-3 border-b space-y-1.5">
                                            <p className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1"><Wallet size={10} /> Desglose Pagos</p>
                                            {(cashPayments + intCashTotal) > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500 flex items-center gap-1"><Banknote size={12} className="text-green-500" /> Efectivo:</span>
                                                    <span className="font-bold text-green-700">{formatCurrency(cashPayments + intCashTotal)}</span>
                                                </div>
                                            )}
                                            {transferPayments > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500 flex items-center gap-1"><Smartphone size={12} className="text-blue-500" /> Transferencia:</span>
                                                    <span className="font-bold text-blue-700">{formatCurrency(transferPayments)}</span>
                                                </div>
                                            )}
                                            {electronicPayments > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500 flex items-center gap-1"><CreditCard size={12} className="text-purple-500" /> Electrónico:</span>
                                                    <span className="font-bold text-purple-700">{formatCurrency(electronicPayments)}</span>
                                                </div>
                                            )}
                                            {otherPayments > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Otros:</span>
                                                    <span className="font-bold text-gray-700">{formatCurrency(otherPayments)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between text-sm pt-1 border-t border-dashed">
                                                <span className="font-bold text-gray-700">Total Pagos:</span>
                                                <span className="font-black text-gray-900">{formatCurrency(cashPayments + intCashTotal + transferPayments + electronicPayments + otherPayments)}</span>
                                            </div>
                                        </div>

                                        {/* Expenses */}
                                        <div className="pb-3 border-b space-y-1.5">
                                            <p className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1"><DollarSign size={10} className="text-yellow-500" /> Gastos</p>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Gastos Ruta (aprobados):</span>
                                                <span className="font-bold text-orange-600">-{formatCurrency(totalApprovedExpenses)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Gastos Rechazados:</span>
                                                <span className="font-bold text-red-600">+{formatCurrency(totalRejected)}</span>
                                            </div>
                                            {addlExp > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Gastos Adicionales:</span>
                                                    <span className="font-bold text-orange-600">-{formatCurrency(addlExp)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between text-sm pt-1 border-t border-dashed">
                                                <span className="font-bold text-gray-700">Total Gastos:</span>
                                                <span className="font-black text-orange-700">-{formatCurrency(totalAllExpenses)}</span>
                                            </div>
                                        </div>

                                        {/* Cash breakdown & 4-way comparison */}
                                        {(() => {
                                            // Messenger-reported cash (sum of reporter_data.cash per order)
                                            const messengerReportedCash = orders.reduce((sum, o) =>
                                                sum + (o.reporter_data ? parseFloat(o.reporter_data.cash || 0) : 0), 0);
                                            const messengerReportedTransfer = orders.reduce((sum, o) =>
                                                sum + (o.reporter_data ? parseFloat(o.reporter_data.transfer || 0) : 0), 0);

                                            return (
                                                <>
                                                    <div className="pb-3 border-b space-y-1.5">
                                                        <p className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1"><Banknote size={10} className="text-green-500" /> Cruce de Efectivo</p>

                                                        {/* Row 1: RC Applied (Siigo FV + INT) */}
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-gray-500" title="Efectivo real de pedidos Siigo + INT">Efectivo Total a Cobrar:</span>
                                                            <span className="font-black text-green-700">{formatCurrency(cashPayments + intCashTotal)}</span>
                                                        </div>

                                                        {/* Row 2: Messenger reported per-order */}
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-gray-500" title="Suma del efectivo reportado por el mensajero en cada pedido">Reportado x Pedido (Msj):</span>
                                                            <span className={`font-bold ${Math.abs(messengerReportedCash - (cashPayments + intCashTotal)) < 1 ? 'text-green-700' : 'text-orange-600'}`}>
                                                                {formatCurrency(messengerReportedCash)}
                                                            </span>
                                                        </div>

                                                        {/* Row 3: Messenger declared total */}
                                                        <div className="flex justify-between text-sm pt-1 border-t border-dashed">
                                                            <span className="font-bold text-gray-700 text-sm">Caja General (Msj):</span>
                                                            <span className={`font-black text-lg ${Math.abs(parseFloat(selectedRoute.declared_cash) - (cashPayments + intCashTotal)) < 1 ? 'text-green-700' : 'text-orange-600'}`}>
                                                                {formatCurrency(selectedRoute.declared_cash)}
                                                            </span>
                                                        </div>

                                                        {/* Row 4: System expected */}
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-gray-500" title="Efectivo esperado calculado por el sistema">Efectivo Sistema:</span>
                                                            <span className="font-bold text-gray-900">{formatCurrency(selectedRoute.expected_cash)}</span>
                                                        </div>

                                                        {/* Differences */}
                                                        {Math.abs(cashPayments - parseFloat(selectedRoute.declared_cash)) >= 1 && (
                                                            <div className="flex justify-between text-xs bg-yellow-50 p-1.5 rounded border border-yellow-200">
                                                                <span className="text-yellow-700 font-bold">RC vs Declarado:</span>
                                                                <span className="text-red-600 font-black">
                                                                    {formatCurrency(cashPayments - parseFloat(selectedRoute.declared_cash))}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {Math.abs(messengerReportedCash - cashPayments) >= 1 && (
                                                            <div className="text-xs bg-orange-50 p-2 rounded border border-orange-200 mt-1">
                                                                <div className="flex justify-between">
                                                                    <span className="text-orange-700 font-bold">⚠️ Diferencia Msj vs RC:</span>
                                                                    <span className="text-orange-800 font-black">
                                                                        {formatCurrency(messengerReportedCash - cashPayments)}
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] text-orange-600 mt-1">
                                                                    El mensajero reportó {formatCurrency(messengerReportedCash)} como efectivo, pero los RCs registran {formatCurrency(cashPayments)}. Posibles causas: método de pago cambiado post-entrega o error del mensajero al seleccionar medio de pago.
                                                                </p>
                                                            </div>
                                                        )}

                                                        {messengerReportedTransfer > 0 && (
                                                            <div className="flex justify-between text-xs bg-blue-50 p-1.5 rounded border border-blue-200 mt-1">
                                                                <span className="text-blue-700 font-bold">Transferencias reportadas (Msj):</span>
                                                                <span className="text-blue-800 font-black">{formatCurrency(messengerReportedTransfer)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Net cash after expenses */}
                                                    {(() => {
                                                        // Calculate INT cash: sum of internal_invoice_value for cash-method orders
                                                        const cashMethodsForInt = ['efectivo', 'efectivo_ruta_domicilio', 'contraentrega'];
                                                        const intCashTotal = orders.reduce((sum, order) => {
                                                            if (order.delivery_status === 'failed') return sum;
                                                            if (cashMethodsForInt.includes(order.payment_method) && parseFloat(order.internal_invoice_value || 0) > 0) {
                                                                return sum + parseFloat(order.internal_invoice_value || 0);
                                                            }
                                                            return sum;
                                                        }, 0);
                                                        const intOrderCount = orders.filter(o => 
                                                            o.delivery_status !== 'failed' && 
                                                            cashMethodsForInt.includes(o.payment_method) && 
                                                            parseFloat(o.internal_invoice_value || 0) > 0
                                                        ).length;
                                                        
                                                        // 🛡️ cashExpectedFromOrders ONLY has the Siigo RCs, it NEVER had INTs.
                                                        const siigoCash = cashExpectedFromOrders; 
                                                        const siigoOrderCount = orders.filter(o => 
                                                            o.delivery_status !== 'failed' && 
                                                            cashMethodsForInt.includes(o.payment_method)
                                                        ).length;

                                                        const totalEfectivo = siigoCash + intCashTotal;
                                                        const verdaderoNeto = totalEfectivo - totalAllExpenses;

                                                        return (
                                                            <div className="pb-3 border-b space-y-1.5">
                                                                <p className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1"><DollarSign size={10} className="text-blue-500" /> Efectivo Neto</p>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-500" title="Pedidos FV con RC de efectivo en Siigo">
                                                                        Pedidos Siigo ({siigoOrderCount}):
                                                                    </span>
                                                                    <span className="font-bold text-green-700">{formatCurrency(siigoCash)}</span>
                                                                </div>
                                                                {intCashTotal > 0 && (
                                                                    <div className="flex justify-between text-sm">
                                                                        <span className="text-gray-500" title="Pedidos INT incluidos en la ruta — no van a Siigo">
                                                                            Pedidos INT ({intOrderCount}):
                                                                        </span>
                                                                        <span className="font-bold text-indigo-600">+{formatCurrency(intCashTotal)}</span>
                                                                    </div>
                                                                )}
                                                                <div className="flex justify-between text-sm pt-1 border-t border-dotted border-gray-200">
                                                                    <span className="text-gray-600 font-semibold">Total Efectivo a Cobrar:</span>
                                                                    <span className="font-black text-green-800">{formatCurrency(totalEfectivo)}</span>
                                                                </div>
                                                                <div className="flex justify-between text-sm">
                                                                    <span className="text-gray-500">(-) Total Gastos:</span>
                                                                    <span className="font-bold text-orange-600">-{formatCurrency(totalAllExpenses)}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center pt-1 border-t border-dashed">
                                                                    <span className="font-bold text-gray-700 text-sm">Neto a Recibir:</span>
                                                                    <span className="font-black text-lg text-blue-700">{formatCurrency(verdaderoNeto)}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            );
                                        })()}

                                        {/* Gastos Adicionales input */}
                                        <div className="border-b pb-3">
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                                                <DollarSign size={12} className="text-orange-500" /> Gastos Adicionales Entrega
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                                        <span className="text-gray-400 text-xs">$</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        value={additionalExpenseAmount}
                                                        onChange={(e) => setAdditionalExpenseAmount(e.target.value)}
                                                        className="block w-full pl-5 pr-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                                                        placeholder="0"
                                                        disabled={isSettling}
                                                    />
                                                </div>
                                            </div>
                                            <input
                                                type="text"
                                                value={additionalExpenseDesc}
                                                onChange={(e) => setAdditionalExpenseDesc(e.target.value)}
                                                className="mt-1.5 block w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                                                placeholder="Descripción del gasto adicional..."
                                                disabled={isSettling}
                                            />
                                            {/* Receipt upload - supports Ctrl+V paste, drag-drop, and file selection */}
                                            <div className="mt-2">
                                                <label className="flex items-center gap-1 text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                    <Camera size={10} /> Soporte del gasto {parseFloat(additionalExpenseAmount) > 0 && <span className="text-red-500">*</span>}
                                                </label>
                                                <div
                                                    className={`relative border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${additionalExpenseFile
                                                        ? 'border-green-300 bg-green-50'
                                                        : 'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50'
                                                        }`}
                                                    tabIndex={0}
                                                    onPaste={(e) => {
                                                        const items = e.clipboardData?.items;
                                                        if (!items) return;
                                                        for (const item of items) {
                                                            if (item.type.startsWith('image/')) {
                                                                e.preventDefault();
                                                                const file = item.getAsFile();
                                                                if (file) setAdditionalExpenseFile(file);
                                                                break;
                                                            }
                                                        }
                                                    }}
                                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-500', 'bg-orange-50'); }}
                                                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500', 'bg-orange-50'); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.currentTarget.classList.remove('border-orange-500', 'bg-orange-50');
                                                        const file = e.dataTransfer.files[0];
                                                        if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
                                                            setAdditionalExpenseFile(file);
                                                        }
                                                    }}
                                                    onClick={() => document.getElementById('settlement-expense-receipt')?.click()}
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => setAdditionalExpenseFile(e.target.files[0] || null)}
                                                        className="hidden"
                                                        disabled={isSettling}
                                                        id="settlement-expense-receipt"
                                                    />
                                                    {additionalExpenseFile ? (
                                                        <div className="flex items-center gap-2">
                                                            {additionalExpenseFile.type?.startsWith('image/') && (
                                                                <img
                                                                    src={URL.createObjectURL(additionalExpenseFile)}
                                                                    alt="preview"
                                                                    className="w-12 h-12 object-cover rounded border"
                                                                />
                                                            )}
                                                            <div className="flex-1 text-left">
                                                                <div className="flex items-center gap-1 text-[10px] text-green-600 font-bold">
                                                                    <CheckCircle size={10} />
                                                                    <span className="truncate">{additionalExpenseFile.name}</span>
                                                                </div>
                                                                <p className="text-[9px] text-gray-400">Clic para cambiar</p>
                                                            </div>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setAdditionalExpenseFile(null); }}
                                                                className="text-red-400 hover:text-red-600 p-1"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="py-1">
                                                            <Camera size={16} className="mx-auto text-gray-400 mb-1" />
                                                            <p className="text-[10px] text-gray-500">
                                                                <span className="font-bold text-orange-600">Ctrl+V</span> para pegar · Clic o arrastra
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                                {parseFloat(additionalExpenseAmount) > 0 && !additionalExpenseFile && (
                                                    <p className="mt-0.5 text-[9px] text-red-500 font-bold flex items-center gap-0.5">
                                                        <AlertTriangle size={9} /> Obligatorio adjuntar soporte
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Efectivo Físico Recibido */}
                                        <div className="pt-1">
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Efectivo Físico Recibido (Cajera)
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                </div>
                                                <input
                                                    type="number"
                                                    value={receivedCash}
                                                    onChange={(e) => setReceivedCash(e.target.value)}
                                                    className="block w-full pl-7 pr-12 py-3 border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50 font-bold text-lg"
                                                    placeholder="0"
                                                    disabled={isSettling}
                                                />
                                            </div>
                                            <p className="mt-1 text-[10px] text-gray-400 italic">
                                                Dinero en efectivo contado físicamente por la cajera.
                                            </p>
                                        </div>

                                        {receivedCash !== '' && (
                                            <div className={`p-5 rounded-2xl border-2 flex flex-col gap-4 shadow-sm ${Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) < 1 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg">
                                                        <div>
                                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Esperado (Sistema)</p>
                                                            <p className="text-xs font-bold text-gray-700">{formatCurrency(efectivoNetoEsperado)}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[10px] text-gray-400 uppercase font-bold">Diferencia</p>
                                                            <p className={`text-sm font-black ${Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) < 1 ? 'text-green-600' : 'text-red-600'}`}>
                                                                {parseFloat(receivedCash) - efectivoNetoEsperado > 0 ? '+' : ''}{formatCurrency(parseFloat(receivedCash) - efectivoNetoEsperado)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center bg-white/50 p-2 rounded-lg">
                                                        <div>
                                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Declarado (Mensajero)</p>
                                                            <p className="text-xs font-bold text-gray-700">{formatCurrency(selectedRoute.declared_cash)}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[10px] text-gray-400 uppercase font-bold">Diferencia</p>
                                                            <p className={`text-sm font-black ${Math.abs(parseFloat(receivedCash) - parseFloat(selectedRoute.declared_cash)) < 1 ? 'text-green-600' : 'text-red-600'}`}>
                                                                {parseFloat(receivedCash) - parseFloat(selectedRoute.declared_cash) > 0 ? '+' : ''}{formatCurrency(parseFloat(receivedCash) - parseFloat(selectedRoute.declared_cash))}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) >= 50000 && (
                                                    <div className="bg-red-600 text-white p-3 rounded-xl flex items-center gap-3 animate-pulse">
                                                        <AlertTriangle size={24} />
                                                        <div>
                                                            <p className="text-xs font-black uppercase">Desbalance Crítico</p>
                                                            <p className="text-[10px] opacity-90">La diferencia supera los $50,000. Verifique el recaudo físico.</p>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) < 50000 && Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) >= 1 && (
                                                    <p className="text-[10px] text-red-600 font-bold flex items-center gap-1 px-1">
                                                        <AlertTriangle size={12} /> Descuadre de caja detectado
                                                    </p>
                                                )}

                                                {Math.abs(parseFloat(receivedCash) - efectivoNetoEsperado) < 1 && (
                                                    <p className="text-[10px] text-green-600 font-bold flex items-center gap-1 px-1">
                                                        <CheckCircle size={12} /> Efectivo cuadrado correctamente
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                                                <MessageSquare size={14} /> Notas de Liquidación
                                            </label>
                                            <textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                rows={2}
                                                className="block w-full p-3 border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50"
                                                placeholder="Observaciones sobre la liquidación..."
                                                disabled={isSettling}
                                            />
                                        </div>

                                        <button
                                            onClick={handleSettle}
                                            disabled={isSettling || (expenses.length > 0 && expenses.length > (approvedExpenses.length + rejectedExpenses.length))}
                                            className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 ${isSettling || (expenses.length > 0 && expenses.length > (approvedExpenses.length + rejectedExpenses.length))
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-1 active:scale-95'
                                                }`}
                                        >
                                            {isSettling ? (
                                                <>
                                                    <Loader2 className="animate-spin" /> Procesando...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle size={20} /> FINALIZAR LIQUIDACIÓN
                                                </>
                                            )}
                                        </button>

                                        {expenses.length > 0 && expenses.length > (approvedExpenses.length + rejectedExpenses.length) && (
                                            <p className="text-center text-[10px] text-red-500 font-bold">
                                                * Debe auditar todos los gastos antes de liquidar
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                    <h4 className="text-xs font-black text-blue-700 uppercase mb-2">Recordatorio Cartera</h4>
                                    <ul className="text-[11px] text-blue-800 space-y-2 list-disc pl-4">
                                        <li>Verifique que todos los pedidos de esta ruta hayan sido marcados como pagados en <b>Recaudos de Ruta</b>.</li>
                                        <li>Confirme la entrada física de los productos devueltos en <b>Validar Retornos</b>.</li>
                                        <li>Al liquidar, la ruta pasará a estado <b>Liquidada</b> y se restará la base del saldo del mensajero.</li>
                                    </ul>
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>
            {/* Image Preview Modal */}
            {
                previewImage && (
                    <div
                        className="fixed inset-0 min-h-screen w-full flex items-center justify-center bg-black/95 p-4"
                        style={{ zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                        onClick={() => {
                            console.log('Closing modal');
                            setPreviewImage(null);
                        }}
                    >
                        <button
                            className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all hover:scale-110 active:scale-95 z-[100000]"
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage(null);
                            }}
                        >
                            <X size={32} />
                        </button>

                        <div
                            className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={previewImage}
                                alt="Evidence Preview"
                                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl ring-1 ring-white/20"
                                style={{ display: 'block' }}
                            />

                            <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                                <a
                                    href={previewImage}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-white/70 hover:text-white text-xs font-bold flex items-center gap-2 hover:underline bg-white/10 px-4 py-2 rounded-full transition-all"
                                >
                                    <ExternalLink size={16} /> Abrir original en pestaña nueva
                                </a>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal de Aplicar Pago */}
            <PickupPaymentModal
                isOpen={showPaymentModal}
                order={selectedPaymentOrder}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedPaymentOrder(null);
                }}
                onConfirm={confirmPickupPayment}
            />
        </div >
    );
};

export default RouteSettlementPage;
